/**
 * Die Regeln der Seite „Konto und Zugang" — Vertrauenspersonen, Zuhause,
 * „Für Lotte", Medienplatz.
 *
 * ## Warum das hier steht und nicht in der Komponente
 *
 * Vier Sorten Regeln, von denen jede still falsch sein kann:
 *
 * 1. **Die Vertrauenspersonen sind eine Liste mit Kennungen** — dieselbe
 *    Abwägung wie bei der Packliste und den ZTL-Genehmigungen: Über die Stelle
 *    in der Liste zu arbeiten heißt, dass ein zweites Fenster die Zeile
 *    verschiebt und der Streichknopf die falsche trifft.
 * 2. **„Für Lotte" hat eine Grenze** (500 Zeichen, Vertrag § 1.1). Eine
 *    Zählung, die im Formular steht, sagt bei 501 Zeichen nichts — der Server
 *    antwortet mit 422, und der Text ist getippt.
 * 3. **Zuhause ist entweder ganz da oder gar nicht.** Eine Adresse ohne Punkt
 *    ist als Stationsvorschlag wertlos, ein Punkt ohne Adresse steht im
 *    Reisekopf als Koordinatenpaar. Beides sieht im Formular in Ordnung aus.
 * 4. **Der Medienplatz ist eine Anzeige über zwei Zahlen, von denen eine
 *    fehlen darf.** `limit_mb: null` heißt **kein Kontingent**; als „0 MB frei"
 *    gelesen wäre es die unangenehmste Verwechslung der Seite.
 *
 * Alles unveränderlich: Jede Funktion gibt eine neue Liste zurück.
 */

import type { Speicherstand, User, Vertrauensperson, Zuhause } from '../api';
import { zuletztZuerst } from '../freigabenfolge';

/* ── Vertrauenspersonen ─────────────────────────────────────────────── */

/** Höchstens so viele — eine Merkliste und kein Adressbuch. */
export const MAX_PERSONEN = 20;

/**
 * Eine Kennung aus dem Namen — sprechend, kollisionsfrei durchgezählt.
 *
 * Dieselbe Machart wie `packliste.neueId` und aus demselben Grund: Steht in
 * einem Protokoll `susanne-2`, weiß man, welche Zeile gemeint ist; ein
 * gewürfeltes `crypto.randomUUID()` sagt darüber nichts und machte diese
 * Datei ohne Browser unprüfbar.
 */
export function neueId(name: string, vorhanden: Iterable<string> = []): string {
  const belegt = new Set(vorhanden);
  const kern = kennung(name) || 'person';
  if (!belegt.has(kern)) return kern;
  for (let n = 2; ; n++) {
    const versuch = `${kern}-${n}`;
    if (!belegt.has(versuch)) return versuch;
  }
}

/** Text zu Kennung — Umlaute ausgeschrieben, sonst würde aus „Jürgen" `j-rgen`. */
function kennung(text: string): string {
  return text
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32)
    .replace(/-+$/g, '');
}

/** Die Liste in eine Form bringen, in der jeder Eintrag eine Kennung hat. */
export function personenGelesen(
  roh: readonly Vertrauensperson[] | null | undefined
): Vertrauensperson[] {
  const ids: string[] = [];
  return (roh ?? []).map((p) => {
    const id = p.id && !ids.includes(p.id) ? p.id : neueId(p.name ?? '', ids);
    ids.push(id);
    return {
      id,
      name: p.name ?? '',
      kontakt: p.kontakt ?? null,
      standort: p.standort === true
    };
  });
}

/**
 * Eine Person anhängen. Ein leerer Name ergibt keine Zeile.
 *
 * Kein Verschmelzen nach Namen: Zwei Menschen können gleich heißen, und der
 * Kontakt unterscheidet sie. Was zusammenfällt, entscheidet der Mensch, indem
 * er eine Zeile streicht.
 */
export function personErgaenzt(
  personen: readonly Vertrauensperson[],
  neu: { name: string; kontakt?: string | null; standort?: boolean }
): Vertrauensperson[] {
  const name = neu.name.trim();
  if (!name) return [...personen];
  return [
    ...personen,
    {
      id: neueId(name, personen.map((p) => p.id)),
      name,
      kontakt: (neu.kontakt ?? '').trim() || null,
      standort: neu.standort ?? true
    }
  ];
}

/** Ein Feld ändern. `id` ist ausgenommen — sie ist der Griff. */
export function personGeaendert(
  personen: readonly Vertrauensperson[],
  id: string,
  patch: Partial<Omit<Vertrauensperson, 'id'>>
): Vertrauensperson[] {
  return personen.map((p) => (p.id === id ? { ...p, ...patch } : p));
}

/** Eine Person streichen. */
export function personGestrichen(
  personen: readonly Vertrauensperson[],
  id: string
): Vertrauensperson[] {
  return personen.filter((p) => p.id !== id);
}

/** Wieder einsetzen — an der alten Stelle, geklemmt. */
export function personEingesetzt(
  personen: readonly Vertrauensperson[],
  eintrag: Vertrauensperson,
  stelle: number
): Vertrauensperson[] {
  if (personen.some((p) => p.id === eintrag.id)) return [...personen];
  const i = Math.max(0, Math.min(stelle, personen.length));
  return [...personen.slice(0, i), eintrag, ...personen.slice(i)];
}

/** Der erste Listenfehler, oder `null`. */
export function personenfehler(personen: readonly Vertrauensperson[]): string | null {
  if (personen.length > MAX_PERSONEN) {
    return `Mehr als ${MAX_PERSONEN} Vertrauenspersonen nimmt der Server nicht an.`;
  }
  if (personen.some((p) => !p.name.trim())) return 'Eine Zeile hat keinen Namen.';
  return null;
}

/** Die Liste, wie sie zum Server geht — getrimmt, Namenlose fallen heraus. */
export function personenServerform(
  personen: readonly Vertrauensperson[]
): Vertrauensperson[] {
  return personen
    .filter((p) => p.name.trim())
    .map((p) => ({
      id: p.id,
      name: p.name.trim(),
      kontakt: (p.kontakt ?? '').trim() || null,
      standort: p.standort === true
    }));
}

/**
 * Der Satz, mit dem der Packlistenposten die Namen nennt.
 *
 * Er steht hier und nicht in der Packliste, weil er von den
 * Vertrauenspersonen handelt und nicht vom Packen — und weil er die Stelle
 * ist, an der man **sieht**, wofür die Liste gut ist. Ein Feld, dessen
 * Wirkung nirgends auftaucht, ist ein Feld, das leer bleibt.
 *
 * Genannt werden nur die mit `standort`: Wer auf der Liste steht, aber den
 * Standort nicht bekommen soll, gehört nicht in diesen Satz.
 */
export function standortsatz(
  personen: readonly Vertrauensperson[] | null | undefined
): string | null {
  const namen = (personen ?? []).filter((p) => p.standort).map((p) => p.name.trim()).filter(Boolean);
  if (!namen.length) return null;
  if (namen.length === 1) return `Standort für ${namen[0]} freigegeben?`;
  const letzter = namen[namen.length - 1];
  return `Standort für ${namen.slice(0, -1).join(', ')} und ${letzter} freigegeben?`;
}

/* ── Zuhause ────────────────────────────────────────────────────────── */

/**
 * Ist das ein brauchbares Zuhause?
 *
 * Brauchbar heißt: Es taugt als erste und letzte Station. Dafür braucht es
 * einen **Punkt**; die Adresse ist der Text darüber. Ein Eintrag ohne
 * Koordinate wäre ein Vorschlag, den der Router nicht annehmen kann — und der
 * Schalter „Zuhause als Start und Ziel" (Runde 2) stünde dann da und täte
 * nichts.
 */
export function zuhauseVollstaendig(z: Zuhause | null | undefined): boolean {
  return !!z && z.lat !== null && z.lat !== undefined && z.lon !== null && z.lon !== undefined;
}

/**
 * Die Zeile, die das hinterlegte Zuhause beschreibt.
 *
 * Die Koordinate steht dabei, aber hinten: Sie ist der Beweis, dass ein Punkt
 * da ist, und der Grund, aus dem man die Zeile überhaupt liest, ist die
 * Adresse.
 */
export function zuhausezeile(z: Zuhause | null | undefined): string | null {
  if (!z) return null;
  const teile: string[] = [];
  if (z.adresse) teile.push(z.adresse);
  if (zuhauseVollstaendig(z)) {
    teile.push(`${(z.lat as number).toFixed(4)}, ${(z.lon as number).toFixed(4)}`);
  }
  return teile.length ? teile.join(' · ') : null;
}

/* ── Für Lotte ──────────────────────────────────────────────────────── */

/** Die Grenze aus dem Vertrag (`VARCHAR(500)`). */
export const LOTTE_MAX = 500;

/**
 * Was noch hineinpasst — und ob es schon zu viel ist.
 *
 * Gezählt werden Zeichen und nicht Wörter, weil die Spalte Zeichen zählt. Die
 * Zahl steht unter dem Feld, sobald es eng wird: Ein Zähler, der von Anfang
 * an mitläuft, macht aus drei Sätzen über sich eine Übung im Kürzen.
 */
export function lotteRest(text: string): { rest: number; zuViel: boolean } {
  const rest = LOTTE_MAX - text.length;
  return { rest, zuViel: rest < 0 };
}

/** Der Wert, wie er zum Server geht. Leer heißt `null` und nicht `""`. */
export function lotteServerform(text: string): string | null {
  return text.trim() || null;
}

/* ── Medienplatz ────────────────────────────────────────────────────── */

/**
 * Der Speicherstand, notfalls aus dem Konto.
 *
 * `GET /auth/me/speicher` ist neu (Vertrag § 4.1, Punkt 5). Ein Server ohne
 * diesen Weg antwortet mit 404, und dann stehen die zwei Zahlen aus
 * `/auth/me` da — die gibt es seit dem Mehrbenutzer-Betrieb. Eine leere
 * Fläche wäre die falsche Antwort: Sie sähe aus wie „nichts belegt".
 */
export function speicherAusKonto(user: User | null): Speicherstand | null {
  if (!user) return null;
  if (user.medien_belegt_mb === undefined && user.medien_limit_mb === undefined) return null;
  return {
    belegt_mb: user.medien_belegt_mb ?? 0,
    limit_mb: user.medien_limit_mb ?? null
  };
}

/**
 * Die Zeile über dem Balken: `312 von 2048 MB belegt · 15 %`.
 *
 * **`limit_mb: null` heißt kein Kontingent** und nicht „null MB". Der Satz
 * sagt das aus, statt eine Prozentzahl zu erfinden, die durch null teilt.
 */
export function speicherzeile(s: Speicherstand | null): string {
  if (!s) return 'Der Medienplatz ist gerade nicht abrufbar.';
  const belegt = `${mbwort(s.belegt_mb)} belegt`;
  if (s.limit_mb === null || s.limit_mb === undefined) {
    return `${belegt} · kein Kontingent hinterlegt`;
  }
  const anteil = s.limit_mb > 0 ? Math.round((s.belegt_mb / s.limit_mb) * 100) : 0;
  return `${belegt} von ${mbwort(s.limit_mb)} · ${anteil} %`;
}

/**
 * Der Anteil für den Balken, 0 bis 1 — oder `null` ohne Kontingent.
 *
 * Geklemmt: Ein Konto, dessen Kontingent nachträglich gesenkt wurde, liegt
 * über 100 %, und ein Balken, der aus seiner Bahn läuft, ist ein Layoutfehler
 * mit einer wahren Aussage darin. Die Aussage steht im Satz daneben.
 */
export function speicheranteil(s: Speicherstand | null): number | null {
  if (!s || s.limit_mb === null || s.limit_mb === undefined || s.limit_mb <= 0) return null;
  return Math.max(0, Math.min(1, s.belegt_mb / s.limit_mb));
}

/** MB oder GB, je nachdem, was sich lesen lässt. */
export function mbwort(mb: number): string {
  if (mb >= 1024) return `${(mb / 1024).toFixed(1).replace('.', ',')} GB`;
  return `${Math.round(mb)} MB`;
}

/* ── Freigaben aller Reisen ─────────────────────────────────────────── */

/**
 * Eine Freigabezeile über alle Reisen hinweg.
 *
 * **Es gibt keinen Sammelendpunkt** — der Vertrag verlangt ausdrücklich, das
 * zu prüfen (§ 4.1, Punkt 5), und die Prüfung sagt: Der Server kennt nur
 * `GET /trips/{id}/freigaben` (und `DELETE …/{share_id}` zum Widerrufen). Die
 * Kontoseite holt deshalb die Reiseliste und dann je Reise die Freigaben.
 * Bei drei Reisen sind das vier Anfragen; sie laufen erst, wenn der Block
 * aufgeklappt wird, und nicht beim Öffnen der Seite.
 *
 * Der Reisename gehört an die Zeile, weil er sonst fehlt: Die Antwort des
 * Servers kennt ihn nicht, sie handelt von der Freigabe.
 */
export interface Freigabezeile {
  tripId: number;
  reiseName: string;
  id: number;
  name: string | null;
  widerrufen: boolean;
  aufrufe: number;
  zuletzt: string | null;
}

/**
 * Die Zeilen sortieren: offene vor widerrufenen, dann nach zuletzt gelesen,
 * dann nach Reise.
 *
 * Offene zuerst, weil sie die Frage beantworten, die man hier stellt — „wer
 * kann gerade mitlesen?". Widerrufene bleiben stehen, weil sie der Beleg
 * sind, dass es einmal so war (dieselbe Entscheidung wie in
 * `Freigaben.svelte`: widerrufen statt löschen).
 */
export function freigabenSortiert(zeilen: readonly Freigabezeile[]): Freigabezeile[] {
  return [...zeilen].sort(
    (a, b) =>
      Number(a.widerrufen) - Number(b.widerrufen) ||
      // Seit dem 20.9.2026: wer zuletzt gelesen hat, oben — quer über alle
      // Reisen, denn die Frage hier ist „wer liest gerade mit?“ und nicht
      // „welche Reise kommt im Alphabet zuerst?“. Nie gelesene danach, wie
      // bisher nach Reise (`freigabenfolge.zuletztZuerst`).
      zuletztZuerst(a.zuletzt, b.zuletzt) ||
      a.reiseName.localeCompare(b.reiseName, 'de') ||
      a.id - b.id
  );
}

/** `2 offen · 1 widerrufen` — der Kopf des zugeklappten Blocks. */
export function freigabenstand(
  zeilen: readonly Freigabezeile[] | null,
  laeuft: boolean
): string {
  if (laeuft) return 'lädt …';
  if (zeilen === null) return 'noch nicht geholt';
  const offen = zeilen.filter((z) => !z.widerrufen).length;
  const weg = zeilen.length - offen;
  if (!zeilen.length) return 'keine Freigabe';
  return weg ? `${offen} offen · ${weg} widerrufen` : `${offen} offen`;
}
